import { useEffect } from "react";
import {
  BasicDNSRecord,
  CaaDNSRecord,
  DNSRecord,
  HttpsDNSRecord,
  PriorityDNSRecord,
  RoutingDNSRecord,
  SrvDNSRecord,
  TlsaDNSRecord,
} from "../../api/partner/models/DNSZone";

const BASE_LABEL_STYLE =
  "flex-1 h-full font-mono tracking-tighter flex items-stretch border border-transparent hover:bg-slate-50 rounded-sm focus-within:border-brand-500";
const BASE_INPUT_STYLE = "px-2 flex-1 w-auto outline-hidden bg-transparent";

export const RecordContentInputs = ({
  value,
  onChange,
  readonlyMode,
}: {
  value: DNSRecord;
  domain: string;
  onChange: (name: Partial<DNSRecord>) => void;
  readonlyMode: boolean;
}) => {
  useEffect(() => {
    if (value.type === "MX" && /^\d+\s/.test(value.content)) {
      console.log("can parse prio from content");
      const [prio, ...contents] = value.content.split(" ");
      onChange({ priority: parseInt(prio), content: contents.join("").trim() });
    }
  }, [value, onChange]);
  switch (value.type) {
    case "A":
    case "AAAA":
    case "CNAME":
      return <RoutingRecordEditor readonlyMode={readonlyMode} record={value} onChange={onChange} />;
    case "NS":
      return <SimpleRecordEditor readonlyMode={readonlyMode} record={value} onChange={onChange} />;
    case "TXT":
      return <TextAreaRecordEditor readonlyMode={readonlyMode} record={value} onChange={onChange} />;
    case "MX":
      return <PriorityRecordEditor readonlyMode={readonlyMode} record={value} onChange={onChange} />;
    case "REDIRECT":
      return <RedirectRecordEditor readonlyMode={readonlyMode} record={value} onChange={onChange} />;
    case "CAA":
      return <CaaRecordEditor readonlyMode={readonlyMode} record={value} onChange={onChange} />;
    case "SRV":
      return <SrvRecordEditor readonlyMode={readonlyMode} record={value} onChange={onChange} />;
    case "TLSA":
      return <TlsaRecordEditor readonlyMode={readonlyMode} record={value} onChange={onChange} />;
    case "HTTPS":
      return <HttpsRecordEditor readonlyMode={readonlyMode} record={value} onChange={onChange} />;
    default:
      return <span className={"italic text-slate-700"}>Unsupported record type...</span>;
  }
};

function SimpleRecordEditor(props: {
  record: BasicDNSRecord;
  onChange: (record: Partial<DNSRecord>) => void;
  readonlyMode: boolean;
}) {
  return (
    <div className={"flex h-full"}>
      <label className={BASE_LABEL_STYLE}>
        <input
          className={BASE_INPUT_STYLE}
          readOnly={props.readonlyMode}
          placeholder={"..."}
          type="text"
          value={props.record.content}
          onChange={(e) => props.onChange({ content: e.target.value })}
        />
      </label>
      <TTLSelect
        value={props.record.ttl}
        readonlyMode={props.readonlyMode}
        onChange={(value) => props.onChange({ ttl: value })}
      />
    </div>
  );
}

function RoutingRecordEditor(props: {
  record: RoutingDNSRecord;
  onChange: (record: Partial<RoutingDNSRecord>) => void;
  readonlyMode: boolean;
}) {
  return (
    <div className={"flex h-full"}>
      <label className={BASE_LABEL_STYLE}>
        <input
          className={BASE_INPUT_STYLE}
          readOnly={props.readonlyMode}
          placeholder={"..."}
          type="text"
          value={props.record.content}
          onChange={(e) => props.onChange({ content: e.target.value })}
        />
      </label>
      <ProxiedSelect
        value={props.record.proxied}
        readonlyMode={props.readonlyMode}
        onChange={(value) => props.onChange({ proxied: value })}
      />
      <TTLSelect
        value={props.record.ttl}
        readonlyMode={props.readonlyMode}
        onChange={(value) => props.onChange({ ttl: value })}
      />
    </div>
  );
}

function TextAreaRecordEditor(props: {
  record: BasicDNSRecord;
  onChange: (record: Partial<DNSRecord>) => void;
  readonlyMode: boolean;
}) {
  return (
    <div className={"flex h-full"}>
      <label className={BASE_LABEL_STYLE}>
        <textarea
          readOnly={props.readonlyMode}
          className={`${BASE_INPUT_STYLE} h-10 p-2.5 focus:h-24 transition-all`}
          value={props.record.content}
          onChange={(e) => props.onChange({ content: e.target.value })}
        />
      </label>
      <TTLSelect
        value={props.record.ttl}
        readonlyMode={props.readonlyMode}
        onChange={(value) => props.onChange({ ttl: value })}
      />
    </div>
  );
}

function PriorityRecordEditor(props: {
  record: PriorityDNSRecord;
  onChange: (record: Partial<DNSRecord>) => void;
  readonlyMode: boolean;
}) {
  return (
    <div className={"flex h-full"}>
      <label className={`${BASE_LABEL_STYLE}`}>
        <input
          className={BASE_INPUT_STYLE}
          placeholder={"..."}
          readOnly={props.readonlyMode}
          type="text"
          value={props.record.content}
          onChange={(e) => props.onChange({ content: e.target.value })}
        />
      </label>
      <label
        className={
          "h-full flex items-stretch border border-transparent rounded-sm hover:bg-slate-50 focus-within:border-brand-500"
        }
      >
        <span className={"text-sm font-medium text-slate-500 uppercase flex items-center px-2"}>PRIO: </span>
        {props.readonlyMode ? (
          <span className={"px-2 flex-1 w-12 outline-hidden bg-transparent flex items-center"}>
            {props.record.priority}
          </span>
        ) : (
          <>
            <input
              className={"pr-2 flex-1 font-mono tracking-tighter w-12 outline-hidden bg-transparent"}
              placeholder={"..."}
              min={0}
              type="number"
              value={props.record.priority}
              onChange={(e) => props.onChange({ priority: Number(e.target.value) })}
            />
          </>
        )}
      </label>
      <TTLSelect
        value={props.record.ttl}
        readonlyMode={props.readonlyMode}
        onChange={(value) => props.onChange({ ttl: value })}
      />
    </div>
  );
}

function RedirectRecordEditor(props: {
  record: PriorityDNSRecord;
  onChange: (record: Partial<DNSRecord>) => void;
  readonlyMode: boolean;
}) {
  return (
    <div className={"flex h-full"}>
      <label className={BASE_LABEL_STYLE}>
        <input
          className={BASE_INPUT_STYLE}
          readOnly={props.readonlyMode}
          placeholder={"..."}
          type="url"
          value={props.record.content}
          onChange={(e) => props.onChange({ content: e.target.value, ttl: 1 })}
        />
      </label>
      <label
        className={
          "h-full flex items-stretch border border-transparent rounded-sm hover:bg-slate-50 focus-within:border-brand-500"
        }
      >
        <span className={"text-sm font-medium text-slate-500 uppercase flex items-center px-2"}>Type: </span>
        {props.readonlyMode ? (
          <span className={"px-2 flex-1 w-30 flex items-center outline-hidden bg-transparent"}>
            {props.record.priority === 301 ? "Permanent" : "Temporary"}
          </span>
        ) : (
          <>
            <select
              className={"pr-2 flex-1 w-30 outline-hidden bg-transparent"}
              value={props.record.priority}
              onChange={(e) => props.onChange({ priority: Number(e.target.value) })}
            >
              <option value="301">Permanent</option>
              <option value="302">Temporary</option>
            </select>
          </>
        )}
      </label>
    </div>
  );
}

function CaaRecordEditor(props: {
  record: CaaDNSRecord;
  onChange: (record: Partial<DNSRecord>) => void;
  readonlyMode: boolean;
}) {
  return (
    <div className={"flex h-full"}>
      <label
        className={
          "h-full flex items-stretch border border-transparent rounded-sm hover:bg-slate-50 focus-within:border-brand-500"
        }
      >
        <span className={"text-sm font-medium text-slate-500 uppercase flex items-center px-2"}>FLAG: </span>
        {props.readonlyMode ? (
          <span className={"px-2 flex-1 w-12 outline-hidden bg-transparent flex items-center"}>
            {props.record.flags}
          </span>
        ) : (
          <>
            <select
              className={"outline-hidden bg-transparent"}
              value={String(props.record.flags)}
              onChange={(e) => props.onChange({ flags: Number(e.target.value) })}
            >
              <option value="0">0</option>
              <option value="128">128</option>
            </select>
          </>
        )}
      </label>
      <label
        className={
          "h-full flex items-stretch border border-transparent rounded-sm hover:bg-slate-50 focus-within:border-brand-500"
        }
      >
        <span className={"text-sm font-medium text-slate-500 uppercase flex items-center px-2"}>TAG: </span>
        {props.readonlyMode ? (
          <span className={"px-2 flex-1 w-12 outline-hidden bg-transparent flex items-center"}>{props.record.tag}</span>
        ) : (
          <>
            <select
              className={"outline-hidden bg-transparent"}
              value={props.record.tag}
              onChange={(e) => props.onChange({ tag: e.target.value })}
            >
              <option value="issue">issue</option>
              <option value="issuewild">issuewild</option>
              <option value="iodef">iodef</option>
            </select>
          </>
        )}
      </label>
      <label className={`${BASE_LABEL_STYLE}`}>
        <input
          className={BASE_INPUT_STYLE}
          placeholder={"..."}
          readOnly={props.readonlyMode}
          type="text"
          value={props.record.value}
          onChange={(e) => props.onChange({ value: e.target.value })}
        />
      </label>
      <TTLSelect
        value={props.record.ttl}
        readonlyMode={props.readonlyMode}
        onChange={(value) => props.onChange({ ttl: value })}
      />
    </div>
  );
}

function SrvRecordEditor(props: {
  record: SrvDNSRecord;
  onChange: (record: Partial<DNSRecord>) => void;
  readonlyMode: boolean;
}) {
  return (
    <div className={"flex flex-col h-20 space-y-1"}>
      <div className={"flex h-full"}>
        <label
          className={
            "h-full flex items-stretch border border-transparent rounded-sm hover:bg-slate-50 focus-within:border-brand-500"
          }
        >
          <span className={"text-sm font-medium text-slate-500 uppercase flex items-center px-2"}>PRIO: </span>
          {props.readonlyMode ? (
            <span className={"px-2 flex-1 w-16 outline-hidden bg-transparent flex items-center"}>
              {props.record.priority}
            </span>
          ) : (
            <>
              <input
                className={"pr-2 flex-1 font-mono tracking-tighter w-16 outline-hidden bg-transparent"}
                placeholder={"..."}
                min={0}
                type="number"
                value={props.record.priority}
                onChange={(e) => props.onChange({ priority: Number(e.target.value) })}
              />
            </>
          )}
        </label>
        <label
          className={
            "h-full flex items-stretch border border-transparent rounded-sm hover:bg-slate-50 focus-within:border-brand-500"
          }
        >
          <span className={"text-sm font-medium text-slate-500 uppercase flex items-center px-2"}>WEIGHT: </span>
          {props.readonlyMode ? (
            <span className={"px-2 flex-1 w-16 outline-hidden bg-transparent flex items-center"}>
              {props.record.weight}
            </span>
          ) : (
            <>
              <input
                className={"pr-2 flex-1 font-mono tracking-tighter w-16 outline-hidden bg-transparent"}
                placeholder={"..."}
                min={0}
                type="number"
                value={props.record.weight}
                onChange={(e) => props.onChange({ weight: Number(e.target.value) })}
              />
            </>
          )}
        </label>
        <label
          className={
            "h-full flex items-stretch border border-transparent rounded-sm hover:bg-slate-50 focus-within:border-brand-500"
          }
        >
          <span className={"text-sm font-medium text-slate-500 uppercase flex items-center px-2"}>PORT: </span>
          {props.readonlyMode ? (
            <span className={"px-2 flex-1 w-16 outline-hidden bg-transparent flex items-center"}>
              {props.record.port}
            </span>
          ) : (
            <>
              <input
                className={"pr-2 flex-1 font-mono tracking-tighter w-16 outline-hidden bg-transparent"}
                placeholder={"..."}
                min={0}
                type="number"
                value={props.record.port}
                onChange={(e) => props.onChange({ port: Number(e.target.value) })}
              />
            </>
          )}
        </label>
      </div>
      <div className={"flex h-full"}>
        <label className={`${BASE_LABEL_STYLE}`}>
          <input
            className={BASE_INPUT_STYLE}
            placeholder={"..."}
            readOnly={props.readonlyMode}
            type="text"
            value={props.record.target}
            onChange={(e) => props.onChange({ target: e.target.value })}
          />
        </label>
        <TTLSelect
          value={props.record.ttl}
          readonlyMode={props.readonlyMode}
          onChange={(value) => props.onChange({ ttl: value })}
        />
      </div>
    </div>
  );
}

function TlsaRecordEditor(props: {
  record: TlsaDNSRecord;
  onChange: (record: Partial<DNSRecord>) => void;
  readonlyMode: boolean;
}) {
  const fields = [
    {
      label: "USAGE",
      key: "usage",
      options: { 0: "0 PKIX-TA", 1: "1 PKIX-EE", 2: "2 DANE-TA", 3: "3 DANE-EE" },
    },
    { label: "SELECTOR", key: "selector", options: { 0: "0 Cert", 1: "1 SPKI" } },
    { label: "MATCH", key: "matchingType", options: { 0: "0 Full", 1: "1 SHA-256", 2: "2 SHA-512" } },
  ] as { label: string; key: "usage" | "selector" | "matchingType"; options: { [key: number]: string } }[];
  return (
    <div className={"flex h-full"}>
      {fields.map((field) => (
        <label
          key={field.key}
          className={
            "h-full flex items-stretch border border-transparent rounded-sm hover:bg-slate-50 focus-within:border-brand-500"
          }
        >
          <span className={"text-sm font-medium text-slate-500 uppercase flex items-center px-2"}>{field.label}: </span>
          {props.readonlyMode ? (
            <span className={"px-2 flex-1 outline-hidden bg-transparent flex items-center"}>
              {field.options[props.record[field.key]] ?? props.record[field.key]}
            </span>
          ) : (
            <select
              className={"outline-hidden bg-transparent"}
              value={String(props.record[field.key])}
              onChange={(e) => props.onChange({ [field.key]: Number(e.target.value) })}
            >
              {Object.entries(field.options).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          )}
        </label>
      ))}
      <label className={`${BASE_LABEL_STYLE}`}>
        <input
          className={BASE_INPUT_STYLE}
          placeholder={"Certificate hash..."}
          readOnly={props.readonlyMode}
          type="text"
          value={props.record.certificate}
          onChange={(e) => props.onChange({ certificate: e.target.value.replace(/\s/g, "") })}
        />
      </label>
      <TTLSelect
        value={props.record.ttl}
        readonlyMode={props.readonlyMode}
        onChange={(value) => props.onChange({ ttl: value })}
      />
    </div>
  );
}

function HttpsRecordEditor(props: {
  record: HttpsDNSRecord;
  onChange: (record: Partial<DNSRecord>) => void;
  readonlyMode: boolean;
}) {
  return (
    <div className={"flex flex-col space-y-1"}>
      <div className={"flex h-9"}>
        <label
          className={
            "h-full flex items-stretch border border-transparent rounded-sm hover:bg-slate-50 focus-within:border-brand-500"
          }
        >
          <span className={"text-sm font-medium text-slate-500 uppercase flex items-center px-2"}>PRIO: </span>
          {props.readonlyMode ? (
            <span className={"px-2 flex-1 w-16 outline-hidden bg-transparent flex items-center"}>
              {props.record.priority}
            </span>
          ) : (
            <input
              className={"pr-2 flex-1 font-mono tracking-tighter w-16 outline-hidden bg-transparent"}
              placeholder={"..."}
              min={0}
              max={65535}
              type="number"
              value={props.record.priority}
              onChange={(e) => props.onChange({ priority: Number(e.target.value) })}
            />
          )}
        </label>
        <label className={`${BASE_LABEL_STYLE}`}>
          <span className={"text-sm font-medium text-slate-500 uppercase flex items-center px-2"}>TARGET: </span>
          <input
            className={BASE_INPUT_STYLE}
            placeholder={". (same name)"}
            readOnly={props.readonlyMode}
            type="text"
            value={props.record.target}
            onChange={(e) => props.onChange({ target: e.target.value })}
          />
        </label>
      </div>
      <div className={"flex h-9"}>
        <label className={`${BASE_LABEL_STYLE}`}>
          <span className={"text-sm font-medium text-slate-500 uppercase flex items-center px-2"}>PARAMS: </span>
          <input
            className={BASE_INPUT_STYLE}
            placeholder={'alpn="h3,h2"'}
            readOnly={props.readonlyMode}
            type="text"
            value={props.record.value}
            onChange={(e) => props.onChange({ value: e.target.value })}
          />
        </label>
        <TTLSelect
          value={props.record.ttl}
          readonlyMode={props.readonlyMode}
          onChange={(value) => props.onChange({ ttl: value })}
        />
      </div>
      <p className={"px-2 text-xs text-slate-500"}>
        Optional: tells browsers which protocols (e.g. HTTP/3) this domain supports before they connect. Your site
        works over https without this record.
      </p>
    </div>
  );
}

function TTLSelect(props: { value: number; onChange: (value: number) => void; readonlyMode: boolean }) {
  const optionMap = {
    1: "Auto",
    60: "1 min.",
    120: "2 min.",
    300: "5 min.",
    600: "10 min.",
    900: "15 min.",
    1800: "30 min.",
    3600: "1 hour",
    7200: "2 hours",
    18000: "5 hours",
    43200: "12 hours",
    86400: "1 day",
  } as { [key: number]: string };
  if (props.readonlyMode) {
    return (
      <div className={"flex"}>
        <span className={"text-sm font-medium text-slate-500 uppercase flex items-center px-2"}>TTL:</span>
        <span className={"text-sm w-16 flex items-center px-2"}>{optionMap[props.value] ?? props.value}</span>
      </div>
    );
  }
  return (
    <label
      className={
        "h-full flex items-stretch border border-transparent rounded-sm hover:bg-slate-50 focus-within:border-brand-500"
      }
    >
      <span className={"text-sm font-medium text-slate-500 uppercase flex items-center px-2"}>TTL: </span>
      <select
        className={"pr-2 flex-1 w-20 outline-hidden bg-transparent"}
        value={props.value}
        onChange={(e) => props.onChange(Number(e.target.value))}
      >
        {Object.entries(optionMap).map(([value, label]) => (
          <option key={value} value={String(value)}>
            {label}
          </option>
        ))}
      </select>
    </label>
  );
}

function ProxiedSelect(props: { value: boolean; onChange: (value: boolean) => void; readonlyMode: boolean }) {
  if (props.readonlyMode) {
    return (
      <div className="flex items-center">
        <span className="text-sm font-medium text-slate-500 uppercase px-2">Proxy:</span>
        <span className="text-sm px-2">{props.value ? "✓ Proxied" : "✗ Disabled"}</span>
      </div>
    );
  }
  return (
    <label className="h-full flex items-center border border-transparent rounded-sm hover:bg-slate-50 focus-within:border-brand-500 cursor-pointer select-none">
      <span className="text-sm font-medium text-slate-500 uppercase px-2">Proxy:</span>
      <input
        type="checkbox"
        className="mx-2 h-4 w-4 rounded-sm border-slate-300 text-brand-600 focus:ring-brand-500"
        checked={props.value}
        onChange={(e) => props.onChange(e.target.checked)}
      />
    </label>
  );
}
